import React, {FC, useEffect, useMemo, useState} from 'react';
import {
    AvailabilityInterface,
    FormType,
    deliveryOptionsInterface,
    gunOptionsInterface,
    rentalPointDataInterface
} from "../../types/types";
import {useTranslation} from "react-i18next";
import {RentalPointName} from "../../data/RentalPointName";
import {
    format,
    nextDay,
    getDay,
    getHours,
    endOfDay,
    endOfToday,
    endOfTomorrow,
    differenceInCalendarDays, isSameDay, isAfter, addDays
} from 'date-fns';
import {getBookingConfig, showDate} from "../../data/price";
import LoadingIndicator from "../utils/LoadingIndicator";
import {DeliveryManager} from "../../data/DeliveryManager";
import {HolidaysManager} from "../../data/HolidaysManager";
import { setRentalPoint } from '../../stateReducers/deliverySlice';
import { useAppDispatch, useAppSelector } from '../../hooks';

interface RentalPointInterface {
    id: number,
    name: string,
    nameFr: string
}

interface RentalPointProps {
    availability: AvailabilityInterface,
}

const RentalPoint: FC<RentalPointProps> = ({availability}) => {
    const d = useAppDispatch();
    const formType = useAppSelector((state) => {
        return state.delivery.formType;
    });
    const deliveryOptions = useAppSelector((state) => {
        return state.delivery;
    });
    const gunOptions = useAppSelector((state) => {
        return state.gunOptions;
    });
    const formLang = useAppSelector((state) => {
        return state.globals.lang;
    })

    const {t} = useTranslation();
    const [rentalPointsLoading, setRentalPointsLoading] = useState(false);
    const allRentalPoints: RentalPointInterface[] = useMemo<RentalPointInterface[]>(() => {
        return [
            {id: RentalPointName.AALST, name: "Aalst", nameFr: "Alost"},
            {id: RentalPointName.BEERSE, name: "Beerse", nameFr: "Beerse"},
            {id: RentalPointName.BRUGGE, name: "Brugge", nameFr: "Brugge"},
            {id: RentalPointName.BRUSSEL, name: "Brussel", nameFr: "Bruxelles"},
            {id: RentalPointName.GHENT, name: "Gent", nameFr: "Gand"},
            {id: RentalPointName.HASSELT, name: "Hasselt", nameFr: "Hasselt"},
            {id: RentalPointName.HERENT, name: "Herent", nameFr: "Herent"},
            {id: RentalPointName.MERKSEM, name: "Merksem", nameFr: "Merksem"},
            {id: RentalPointName.POELKAPELLE, name: "Poelkapelle", nameFr: "Poelkapelle"}
        ];
    }, []);

    const [availableRentalPoints, setRentalPoints] = useState<RentalPointInterface[]>(allRentalPoints);
    const [rentalPointData, setRentalPointData] = useState<rentalPointDataInterface>({});

    const bookingConfig = getBookingConfig();
    let manager = new DeliveryManager(bookingConfig);
    const holidayManager = new HolidaysManager(bookingConfig, bookingConfig.rentalPointSchedule);

    const isHoliday = (date: Date, point: number) => {
        return !holidayManager.isRentalPointWorking(point, date);
    }

    const getOpenedDates = function (possibleToCollectToday: boolean, possibleToCollectTomorrow: boolean) {
        return function (playStart: Date, pointNumber: number): Date[] {
            const schedule = bookingConfig.rentalPointSchedule[pointNumber];
            const exceptionallyOpenDates = bookingConfig.exceptionallyOpen[pointNumber];
            
            let checkDate = possibleToCollectToday ? endOfToday() : endOfTomorrow();
            
            if (!possibleToCollectTomorrow) {
                // If it isn't possible to collect tomorrow, it also wasn't possible today, so we know we used endOfTomorrow()
                // So we just need the day after that
                checkDate = addDays(checkDate, 1);
            }
            let dateStr = format(checkDate, "yyyy-MM-dd");
            const workingDays: Date[] = [];
            while (isAfter(playStart, checkDate)) {
                if (schedule.includes(getDay(checkDate)) || exceptionallyOpenDates.includes(dateStr)) {
                    workingDays.push(checkDate);
                }
                checkDate = addDays(checkDate, 1);
                dateStr = format(checkDate, "yyyy-MM-dd");
            }
            return workingDays;
        }
    }

    const availableRentalPointsForDate = useMemo<RentalPointInterface[]>(() => {
        if (deliveryOptions.playStart === null) {
            return [];
        }
        const now = new Date();
        const playStart = endOfDay(deliveryOptions.playStart);

        console.log('========availableRentalPoints======', availableRentalPoints);
        return availableRentalPoints
            //if quantity is larger than defined one in config
            //3rd party rental points are not allowed
            .filter((point) => {
                if ('phoenix' === gunOptions.gunType && !manager.allowedToRentInExternalRentalPoints(gunOptions.quantity ?? 0)) {
                    return point.id === RentalPointName.HASSELT || point.id === RentalPointName.HERENT;
                }
                if (formType === 'YouthPromo') {
                    return point.id === RentalPointName.HASSELT || point.id === RentalPointName.HERENT;
                }
                if('childBow' === gunOptions.gunType || 'adultBow' === gunOptions.gunType){
                    return point.id === RentalPointName.HASSELT || point.id === RentalPointName.HERENT;
                }

                return true;
            })
            // Filter out Beerse under certain conditions
            .filter((rentalPoint) => {
                if (rentalPoint.id === RentalPointName.BEERSE) {
                    const now = new Date();
                    // If today is Thursday past 3:30pm, then the start/delivery may not happen before Tuesday
                    // so playStart may not be until Wednesday.
                    const thursday = 4;
                    if (now.getDay() === thursday
                        && ((now.getHours() >= 15 && now.getMinutes() >= 30)
                        || now.getHours() >= 16)) {
                        console.error("In if statement");
                        return ![0, 1, 2, 5, 6].includes(playStart.getDay());
                    }
                }
                return true;
            })
            //find rental points where collection is possible
            //today before 16 for own rental points
            //tomorrow and later for 3rd party rental points
            .filter((point) => {
                console.log(`Point ${point.id}`);
                //iterate over available Rental points

                // Collection today possible if home point and before 16:00
                const isPossibleToCollectToday = (getHours(now) < 16) && (point.id === RentalPointName.HASSELT || point.id === RentalPointName.HERENT);
                // Collection tomorrow possible for any point before 16:00, but, failing that, it's still possible if
                // it's a home point
                const isPossibleToCollectTomorrow = (getHours(now) < 16) || (point.id === RentalPointName.HASSELT || point.id === RentalPointName.HERENT);
                const openedDates = getOpenedDates(isPossibleToCollectToday, isPossibleToCollectTomorrow)(playStart, point.id);
                console.log('got opened dates for point ',point.id, openedDates.map(showDate));
                const exceptionallyOpenDates = getBookingConfig().exceptionallyOpen[point.id];
                console.log("Exceptionally open dates", exceptionallyOpenDates);
                return openedDates.some((dateToCheck: Date) => {
                    console.log('checking possible date', showDate(dateToCheck));
                    const dateStr = format(dateToCheck, "yyyy-MM-dd");
                    console.log(exceptionallyOpenDates.includes(dateStr));
                    if(isHoliday(dateToCheck,point.id) && !exceptionallyOpenDates.includes(dateStr)){
                        console.warn('it is holiday');
                        return false;
                    }

                    return true;
                });
            });
    }, [deliveryOptions.playStart, availableRentalPoints, bookingConfig.rentalPointSchedule, availability]);


    useEffect(() => {
        if (deliveryOptions.playStart === null) {
            setRentalPoints([]);
            return;
        }
        console.log("===All rental points===", allRentalPoints);
        console.log("===Availability===", availability);
        setRentalPoints(allRentalPoints.filter((point: RentalPointInterface) => {
            console.log(`Point ${point.id}: ${availability.hasOwnProperty(point.id) && availability[point.id]}`);
            return availability.hasOwnProperty(point.id) && availability[point.id];
        }));
    }, [allRentalPoints, availability]);

    useEffect(() => {
        if (deliveryOptions.rentalPoint && !rentalPointData.image) {
            setRentalPointDetails(deliveryOptions.rentalPoint);
        }
    });

    let setRentalPointDetails = function (rentalPoint: number) {
        const key = bookingConfig.rentalPointData[rentalPoint].translationKey;
        setRentalPointData({
            image: formLang !== "fr"
                ? bookingConfig.rentalPointData[rentalPoint].image
                : bookingConfig.rentalPointData[rentalPoint].imageFr,
            description: t('rentalPointsSchedule.' + key, {interpolation: {escapeValue: false}})
        });
    };
    const processRentalPointClick = (rentalPoint: number) => {
        d(setRentalPoint(rentalPoint));
        setRentalPointDetails(rentalPoint);
    }

    return (
        <div className="flex justify-between flex-col lg:flex-row mt-2">
            {rentalPointsLoading && <LoadingIndicator text={t('loadingRentalPointsLabel')}/>}
            {!rentalPointsLoading && availableRentalPointsForDate.length > 0 && <div className="w-full lg:w-1/3">
                <legend className="text-base font-normal m-0">{t('delivery.pointText')}</legend>
                {availableRentalPointsForDate
                .sort((a, b) => {
                    if (formLang === "fr") {
                        return a.nameFr < b.nameFr ? -1 : 1;
                    } else {
                        return a.name < b.name ? -1 : 1;
                    }
                })
                .map((point) =>
                    <div className="form-check" key={point.id}>
                        <input
                            className="form-check-input  rounded-full h-4 w-4 border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300"
                            type="radio" name="rentalPoint" value={point.id} id={'point_' + point.id}
                            checked={deliveryOptions.rentalPoint === point.id}
                            onChange={(e) => processRentalPointClick(parseInt(e.currentTarget.value))}/>
                        <label className="form-check-label inline-block text-gray-800"
                               htmlFor={'point_' + point.id}>{formLang !== 'fr' ? point.name : point.nameFr}
                        </label>
                    </div>
                )}
            </div>
            }
            {!rentalPointsLoading && availableRentalPointsForDate.length === 0 && <div>
                {t('delivery.noRentalPoints')}
            </div>}
            {!rentalPointsLoading && deliveryOptions.deliveryType === 'rental' &&
                <div className="flex justify-between flex-col lg:flex-row">
                    {rentalPointData.description && <div dangerouslySetInnerHTML={
                        {__html: rentalPointData.description}}/>
                    }
                    {rentalPointData.image &&
                        <img className="w-full lg:w-1/3 self-center" src={rentalPointData.image}
                             alt={t('rentalPointScheduleImageAlt')}/>}
                </div>
            }
        </div>
    );
};

export default RentalPoint;
